// src/components/BillingReport.jsx
// Platform admin: billing report for one tutor (org), per cohort, from the
// billing enrolment log (onUserEnrolmentChange in functions/index.js).
// Applies the Order Form's rules: each student is charged once per cohort
// (a student moved to another cohort is charged again for that cohort),
// except anyone removed within 14 days of joining; test accounts are never
// charged. "Mark as invoiced" records exactly which students went on an
// invoice (billingInvoices), so later invoices only include new students -
// nobody is charged twice. Also shows AI summary usage per month, for
// information only: it isn't charged to tutors.

import React, { useCallback, useEffect, useState } from 'react';
import { X, Download, ChevronDown, ChevronRight, Sparkles, Receipt } from 'lucide-react';
import { getBillingEnrolments, getAiUsageByMonth, getBillingInvoices, createBillingInvoice, deleteBillingInvoice } from '../firebase/platformAdmin';

const GRACE_DAYS = 14;
const DAY_MS = 24 * 60 * 60 * 1000;
const POUND = '\u00a3';

const toDate = (ts) => (ts && typeof ts.toDate === 'function' ? ts.toDate() : null);
const formatDate = (d) => (d ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

// One student's status in one cohort:
// 'invoiced'     - already on an invoice for this cohort
// 'test'         - flagged as a test account on the admin screen
// 'not_billable' - removed within 14 days of joining, without submitting homework
// 'grace'        - still in, joined under 14 days ago, no homework yet (can't be invoiced yet)
// 'to_invoice'   - billable and not yet invoiced
function rowStatus(enrolment, now, testIds, invoicedAt) {
  if (invoicedAt.has(enrolment.id)) return 'invoiced';
  if (testIds.has(enrolment.studentId)) return 'test';
  const joined = toDate(enrolment.joinedAt);
  const removed = toDate(enrolment.removedAt);
  // Submitted homework = used the service: the 14-day rule no longer applies
  if (!joined || enrolment.firstSubmissionAt) return 'to_invoice';
  if (removed) return removed - joined < GRACE_DAYS * DAY_MS ? 'not_billable' : 'to_invoice';
  return now - joined < GRACE_DAYS * DAY_MS ? 'grace' : 'to_invoice';
}

function statusLabel(status, enrolment, invoicedAt) {
  switch (status) {
    case 'invoiced': return `Invoiced ${formatDate(invoicedAt.get(enrolment.id))}`;
    case 'test': return 'Test account - not billed';
    case 'not_billable': return `Not billed - removed within ${GRACE_DAYS} days, no homework submitted`;
    case 'grace': {
      const joined = toDate(enrolment.joinedAt);
      return `In first ${GRACE_DAYS} days - can be invoiced from ${formatDate(new Date(joined.getTime() + GRACE_DAYS * DAY_MS))}`;
    }
    default: return 'To invoice';
  }
}

function csvCell(value) {
  const text = String(value ?? '');
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function downloadCsv(filename, rows) {
  const blob = new Blob([rows.map(r => r.map(csvCell).join(',')).join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.replace(/[^\w.-]+/g, '_');
  a.click();
  URL.revokeObjectURL(url);
}

function BillingReport({ orgId, orgName, onClose, testIds = new Set(), onToggleTestAccount }) {
  const [enrolments, setEnrolments] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [aiMonths, setAiMonths] = useState([]);
  const [error, setError] = useState('');
  const [pricePerStudent, setPricePerStudent] = useState('4');
  const [expanded, setExpanded] = useState({});
  const [savingCohortId, setSavingCohortId] = useState(null);

  const loadInvoices = useCallback(() => getBillingInvoices(orgId).then(setInvoices), [orgId]);

  useEffect(() => {
    Promise.all([getBillingEnrolments(orgId), getBillingInvoices(orgId), getAiUsageByMonth(orgId).catch(() => [])])
      .then(([e, inv, ai]) => { setEnrolments(e); setInvoices(inv); setAiMonths(ai); })
      .catch(err => {
        console.error('Billing report load error:', err);
        setError('Could not load the billing report.');
      });
  }, [orgId]);

  const now = new Date();
  const price = parseFloat(pricePerStudent) || 0;

  // enrolment id -> date of the invoice it went on
  const invoicedAt = new Map();
  invoices.forEach(inv => (inv.enrolmentIds || []).forEach(id => invoicedAt.set(id, toDate(inv.invoicedAt) || now)));

  const cohorts = enrolments ? Object.values(enrolments.reduce((acc, e) => {
    const c = acc[e.cohortId] || (acc[e.cohortId] = { id: e.cohortId, name: e.cohortName || e.cohortId, rows: [] });
    if (e.cohortName) c.name = e.cohortName;
    c.rows.push(e);
    return acc;
  }, {})).map(c => {
    c.rows.sort((a, b) => (a.studentName || '').localeCompare(b.studentName || ''));
    c.rows.forEach(r => { r.status = rowStatus(r, now, testIds, invoicedAt); });
    const count = (status) => c.rows.filter(r => r.status === status).length;
    c.toInvoice = c.rows.filter(r => r.status === 'to_invoice');
    c.invoicedCount = count('invoiced');
    c.graceCount = count('grace');
    c.testCount = count('test');
    // Manual "Mark as invoiced" records (one cohort) and invoices sent from
    // the Invoicing panel (any number of cohorts - count this cohort's share)
    const rowIds = new Set(c.rows.map(r => r.id));
    c.invoices = invoices
      .filter(inv => inv.cohortId === c.id || (inv.cohortIds || []).includes(c.id))
      .map(inv => inv.kind === 'invoice' ? { ...inv, studentCount: (inv.enrolmentIds || []).filter(id => rowIds.has(id)).length, totalGbp: null } : inv)
      .sort((a, b) => (toDate(b.invoicedAt) || now) - (toDate(a.invoicedAt) || now));
    c.firstJoined = Math.min(...c.rows.map(r => toDate(r.joinedAt)?.getTime() || Infinity));
    c.hasBackfilled = c.rows.some(r => r.backfilled);
    return c;
  }).sort((a, b) => b.firstJoined - a.firstJoined) : [];

  const totalToInvoice = cohorts.reduce((sum, c) => sum + c.toInvoice.length, 0);

  const handleMarkInvoiced = async (cohort) => {
    const n = cohort.toInvoice.length;
    if (!window.confirm(`Record an invoice for ${n} student${n === 1 ? '' : 's'} in ${cohort.name}${price > 0 ? ` at ${POUND}${price.toFixed(2)} each (${POUND}${(n * price).toFixed(2)})` : ''}?\n\nThese students won't show as "to invoice" for this cohort again.`)) return;
    setSavingCohortId(cohort.id);
    try {
      await createBillingInvoice({
        orgId,
        cohortId: cohort.id,
        cohortName: cohort.name,
        enrolmentIds: cohort.toInvoice.map(r => r.id),
        studentCount: n,
        pricePerStudent: price,
        totalGbp: Math.round(n * price * 100) / 100
      });
      await loadInvoices();
    } catch (err) {
      console.error('Record invoice error:', err);
      alert('Could not record the invoice: ' + err.message);
    } finally {
      setSavingCohortId(null);
    }
  };

  const handleUndoInvoice = async (invoice) => {
    if (!window.confirm(`Undo the invoice recorded on ${formatDate(toDate(invoice.invoicedAt))} for ${invoice.studentCount} student${invoice.studentCount === 1 ? '' : 's'}?\n\nThey'll show as "to invoice" again.`)) return;
    try {
      await deleteBillingInvoice(invoice.id);
      await loadInvoices();
    } catch (err) {
      console.error('Undo invoice error:', err);
      alert('Could not undo the invoice: ' + err.message);
    }
  };

  const handleDownloadCsv = (cohort) => {
    downloadCsv(`billing-${orgName}-${cohort.name}.csv`, [
      ['Student', 'Email', 'Joined', 'Left', 'Status'],
      ...cohort.rows.map(r => [r.studentName, r.studentEmail, formatDate(toDate(r.joinedAt)), formatDate(toDate(r.removedAt)), statusLabel(r.status, r, invoicedAt)])
    ]);
  };

  return (
    <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', marginBottom: '16px', border: '2px solid #2b6cb0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <h2 style={{ margin: 0, fontSize: '20px' }}>Billing: {orgName}</h2>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#718096', display: 'flex' }} title="Close">
          <X size={20} />
        </button>
      </div>
      <div style={{ fontSize: '13px', color: '#718096', marginBottom: '16px', lineHeight: 1.6 }}>
        Each student is charged once per cohort, unless they were removed within {GRACE_DAYS} days of joining without submitting any homework. A student moved to another cohort is charged again for that cohort. Once invoiced, a student isn't charged again for the same cohort.
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
          Price per student: {POUND}
          <input
            type="number"
            min="0"
            step="0.5"
            value={pricePerStudent}
            onChange={(e) => setPricePerStudent(e.target.value)}
            style={{ width: '70px', padding: '4px 6px', border: '1px solid #cbd5e0', borderRadius: '6px' }}
          />
          {enrolments !== null && (
            <strong style={{ marginLeft: '12px', color: '#2d3748' }}>
              Ready to invoice across all cohorts: {totalToInvoice} student{totalToInvoice === 1 ? '' : 's'}{price > 0 && ` = ${POUND}${(totalToInvoice * price).toFixed(2)}`}
            </strong>
          )}
        </div>
      </div>

      {error && <div style={{ color: '#c53030', fontSize: '14px' }}>{error}</div>}
      {!error && enrolments === null && <div style={{ color: '#a0aec0', fontSize: '14px' }}>Loading...</div>}
      {enrolments !== null && cohorts.length === 0 && <div style={{ color: '#a0aec0', fontSize: '14px' }}>No enrolments recorded yet.</div>}

      {cohorts.map(cohort => (
        <div key={cohort.id} style={{ borderTop: '1px solid #edf2f7', padding: '10px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setExpanded(prev => ({ ...prev, [cohort.id]: !prev[cohort.id] }))}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '14px', color: '#2d3748' }}
            >
              {expanded[cohort.id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <strong>{cohort.name}</strong>
            </button>
            <span style={{ fontSize: '13px', color: '#4a5568' }}>
              <strong>{cohort.toInvoice.length} to invoice</strong>
              {price > 0 && cohort.toInvoice.length > 0 && ` (${POUND}${(cohort.toInvoice.length * price).toFixed(2)})`}
              {cohort.invoicedCount > 0 && ` | ${cohort.invoicedCount} already invoiced`}
              {cohort.graceCount > 0 && ` | ${cohort.graceCount} in first ${GRACE_DAYS} days`}
              {cohort.testCount > 0 && <span style={{ color: '#a0aec0' }}>{` | ${cohort.testCount} test`}</span>}
            </span>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
              <button
                onClick={() => handleDownloadCsv(cohort)}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 12px', background: 'none', border: '1px solid #2b6cb0', color: '#2b6cb0', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
              >
                <Download size={13} /> Student list (CSV)
              </button>
              <button
                onClick={() => handleMarkInvoiced(cohort)}
                disabled={cohort.toInvoice.length === 0 || savingCohortId === cohort.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 12px',
                  background: cohort.toInvoice.length === 0 ? '#e2e8f0' : '#2b6cb0',
                  color: cohort.toInvoice.length === 0 ? '#a0aec0' : 'white',
                  border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600,
                  cursor: cohort.toInvoice.length === 0 ? 'not-allowed' : 'pointer'
                }}
              >
                <Receipt size={13} /> {savingCohortId === cohort.id ? 'Saving...' : `Mark ${cohort.toInvoice.length} as invoiced`}
              </button>
            </div>
          </div>

          {expanded[cohort.id] && cohort.invoices.length > 0 && (
            <div style={{ margin: '10px 0 0 22px', fontSize: '12px', color: '#4a5568' }}>
              {cohort.invoices.map((inv, i) => (
                <div key={inv.id} style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '4px' }}>
                  <Receipt size={12} />
                  {inv.kind === 'invoice' ? `Invoice ${inv.number}` : 'Invoiced'} {formatDate(toDate(inv.invoicedAt))}: {inv.studentCount} student{inv.studentCount === 1 ? '' : 's'}{inv.kind === 'invoice' ? ' in this cohort' : ''}
                  {inv.kind !== 'invoice' && inv.pricePerStudent > 0 && ` at ${POUND}${Number(inv.pricePerStudent).toFixed(2)} = ${POUND}${Number(inv.totalGbp).toFixed(2)}`}
                  {i === 0 && inv.kind !== 'invoice' && (
                    <button
                      onClick={() => handleUndoInvoice(inv)}
                      style={{ padding: '0 8px', background: 'none', border: '1px solid #cbd5e0', borderRadius: '6px', fontSize: '11px', color: '#4a5568', cursor: 'pointer' }}
                    >
                      Undo
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {expanded[cohort.id] && (
            <table style={{ width: '100%', marginTop: '10px', fontSize: '13px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#718096' }}>
                  <th style={{ padding: '4px 8px' }}>Student</th>
                  <th style={{ padding: '4px 8px' }}>Joined</th>
                  <th style={{ padding: '4px 8px' }}>Left</th>
                  <th style={{ padding: '4px 8px' }}>Status</th>
                  <th style={{ padding: '4px 8px' }}></th>
                </tr>
              </thead>
              <tbody>
                {cohort.rows.map(r => (
                  <tr key={r.id} style={{ borderTop: '1px solid #f1f5f9', color: ['not_billable', 'test'].includes(r.status) ? '#a0aec0' : '#2d3748' }}>
                    <td style={{ padding: '4px 8px' }}>{r.studentName}<div style={{ fontSize: '11px', color: '#a0aec0' }}>{r.studentEmail}</div></td>
                    <td style={{ padding: '4px 8px' }}>{formatDate(toDate(r.joinedAt))}{r.backfilled ? ' *' : ''}</td>
                    <td style={{ padding: '4px 8px' }}>{formatDate(toDate(r.removedAt))}</td>
                    <td style={{ padding: '4px 8px', fontWeight: r.status === 'to_invoice' ? 600 : 400 }}>{statusLabel(r.status, r, invoicedAt)}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                      {onToggleTestAccount && r.status !== 'invoiced' && (
                        <button
                          onClick={() => onToggleTestAccount(r.studentId, r.studentName)}
                          style={{ padding: '2px 8px', background: 'none', border: '1px solid #cbd5e0', borderRadius: '6px', fontSize: '11px', color: '#4a5568', cursor: 'pointer', whiteSpace: 'nowrap' }}
                        >
                          {testIds.has(r.studentId) ? 'Not a test account' : 'Mark as test'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {expanded[cohort.id] && cohort.hasBackfilled && (
            <div style={{ fontSize: '11px', color: '#a0aec0', marginTop: '6px' }}>
              * Already in this cohort when the enrolment log started - joined date is when they registered or joined this tutor. Students moved or deleted before the log started aren't listed.
            </div>
          )}
        </div>
      ))}

      <div style={{ borderTop: '1px solid #edf2f7', paddingTop: '12px', marginTop: '6px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: 600, color: '#805ad5', marginBottom: '6px' }}>
          <Sparkles size={14} /> AI summaries by month (for information - not charged to tutors)
        </div>
        {aiMonths.length === 0 ? (
          <div style={{ fontSize: '13px', color: '#a0aec0' }}>No monthly AI usage recorded yet.</div>
        ) : (
          <table style={{ fontSize: '13px', borderCollapse: 'collapse' }}>
            <tbody>
              {aiMonths.map(m => (
                <tr key={m.month}>
                  <td style={{ padding: '2px 16px 2px 0' }}>{m.month}</td>
                  <td style={{ padding: '2px 16px 2px 0' }}>{m.calls} summar{m.calls === 1 ? 'y' : 'ies'}</td>
                  <td style={{ padding: '2px 0' }}>${(m.costUsd || 0).toFixed(3)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default BillingReport;
